import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOverlayCount } from '@/components/ui/overlayStack';
import {
  ancestorScrollsHorizontally,
  browserOwnsOuterSwipeEdge,
  classifySwipeBackMove,
  elementBlocksSwipeBack,
  historyCanGoBack,
  SWIPE_BACK_MIN_DISTANCE_PX,
  swipeBackShouldNavigate,
  swipeBackStartAllowed,
} from '@/lib/swipeBack';

/**
 * Right-swipe from the left side of the screen goes back one history entry.
 * Works in the installed app, where Safari's edge gesture does not exist.
 */
export function SwipeBack() {
  const navigate = useNavigate();
  const indicatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let tracking = false;
    let startX = 0;
    let startY = 0;

    const paint = (dx: number) => {
      const el = indicatorRef.current;
      if (!el) return;
      const progress = Math.max(0, Math.min(1, dx / SWIPE_BACK_MIN_DISTANCE_PX));
      el.style.opacity = progress > 0 ? '1' : '0';
      el.style.transform = `scaleX(${progress})`;
    };

    const reset = () => {
      tracking = false;
      paint(0);
    };

    const onStart = (event: TouchEvent) => {
      reset();
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (!touch) return;
      if (!swipeBackStartAllowed(touch.clientX, browserOwnsOuterSwipeEdge())) return;
      if (!historyCanGoBack(window.history.state)) return;
      if (getOverlayCount() > 0) return;
      if (elementBlocksSwipeBack(event.target)) return;
      if (ancestorScrollsHorizontally(event.target)) return;
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    };

    const onMove = (event: TouchEvent) => {
      if (!tracking || event.touches.length !== 1) {
        if (tracking) reset();
        return;
      }
      const touch = event.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const move = classifySwipeBackMove(dx, dy);
      if (move === 'cancel') {
        reset();
        return;
      }
      if (move === 'back') {
        event.preventDefault();
        paint(dx);
      }
    };

    const onEnd = (event: TouchEvent) => {
      if (!tracking) return;
      const touch = event.changedTouches[0];
      tracking = false;
      paint(0);
      if (!touch || !historyCanGoBack(window.history.state)) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (!swipeBackShouldNavigate(dx, dy)) return;
      navigate(-1);
    };

    document.addEventListener('touchstart', onStart, { capture: true, passive: true });
    document.addEventListener('touchmove', onMove, { capture: true, passive: false });
    document.addEventListener('touchend', onEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', reset, true);
    return () => {
      document.removeEventListener('touchstart', onStart, true);
      document.removeEventListener('touchmove', onMove, true);
      document.removeEventListener('touchend', onEnd, true);
      document.removeEventListener('touchcancel', reset, true);
    };
  }, [navigate]);

  return (
    <div
      ref={indicatorRef}
      aria-hidden
      className='pointer-events-none fixed inset-y-0 left-0 z-[60] w-16 origin-left scale-x-0 bg-gradient-to-r from-black/25 to-transparent opacity-0 dark:from-white/20'
    />
  );
}
